import { CircularProgress } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Field, FieldProps, useFormikContext } from 'formik';
import { TextField } from 'formik-material-ui';
import React, { ChangeEvent, Fragment, SFC, useEffect, useState } from 'react';
import { Participant } from '../../gen/api/api';
import ApiService from '../../services/ApiService';
import useDebounce from '../../shared/hooks/UseDebounce';
import useTSHacks from '../../shared/tshacks/tshacks';
import { Option } from '../field-editors/Option';
import { useSnackbar } from 'notistack';

interface RetailerProps {
    id: string;
    fieldName: string;
    fieldLabel: string;
    fieldOptions?: () => Option[];
    fieldValidator?: (value: any) => string;
    required?: boolean;
}

type RetailerAutocompleteProps = FieldProps & RetailerProps;

const RetailerAutocomplete: SFC<RetailerAutocompleteProps> = ({
    fieldName,
    fieldLabel,
    fieldValidator,
    fieldOptions,
    ...props
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const { values, setFieldValue } = useFormikContext();
    const { getObjectProperty } = useTSHacks();

    const [retailerSelectOpen, setRetailerSelectOpen] = useState(false);
    const [retailerInputValue, setRetailerInputValue] = useState('');

    const debouncedSearchTerm = useDebounce(retailerInputValue, 500);

    const [retailerSelectOptions, setRetailerSelectOptions] = useState<Participant[]>([]);
    const [retailersLoading, setRetailersLoading] = useState(false);

    const [selectedRetailers, setSelectedRetailers] = useState<Participant | null>(null);

    const handleRetailerInputChange = (_event: ChangeEvent<{}>, value: any) => {
        setRetailerInputValue(value);
    };

    useEffect(() => {
        const fetchData = async () => {
            const retailerId = getObjectProperty(values, fieldName);
            if (retailerId) {
                setRetailersLoading(true);
                ApiService()
                    .fetchRetailerById(retailerId)
                    .then((response) => {
                        setSelectedRetailers(response.data);
                    })
                    .catch((_error) =>
                        enqueueSnackbar('Unable to load data for '.concat(fieldLabel).concat('.'), {
                            variant: 'error',
                        }),
                    )
                    .finally(() => setRetailersLoading(false));
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        let active = true;

        (async () => {
            if (debouncedSearchTerm) {
                setRetailersLoading(true);
                ApiService()
                    .fetchRetailers(debouncedSearchTerm)
                    .then((respose) => {
                        const retailers = respose.data;
                        if (active) {
                            setRetailerSelectOptions(retailers);
                        }
                    })
                    .catch((_error) =>
                        enqueueSnackbar('Unable to load suggestions for '.concat(fieldLabel).concat('.'), {
                            variant: 'error',
                        }),
                    )
                    .finally(() => setRetailersLoading(false));
            }
        })();

        return () => {
            active = false;
        };
    }, [debouncedSearchTerm]);

    useEffect(() => {
        if (!retailerSelectOpen) {
            setRetailerSelectOptions([]);
        }
    }, [retailerSelectOpen]);

    return (
        <Autocomplete
            {...props}
            open={retailerSelectOpen}
            onOpen={() => setRetailerSelectOpen(true)}
            onClose={() => setRetailerSelectOpen(false)}
            options={retailerSelectOptions}
            multiple={false}
            getOptionLabel={(option) => option.udk || option.toString()}
            value={selectedRetailers}
            loading={retailersLoading}
            onChange={(_: ChangeEvent<{}>, value: Participant | null) => {
                setSelectedRetailers(value);
                setFieldValue(fieldName, value?.id);
            }}
            onInputChange={handleRetailerInputChange}
            renderInput={(params) => (
                <Field
                    component={TextField}
                    {...params}
                    label={fieldLabel}
                    name={fieldName}
                    variant="filled"
                    fullWidth
                    validate={fieldValidator}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <Fragment>
                                {retailersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </Fragment>
                        ),
                    }}
                />
            )}
        />
    );
};

export default RetailerAutocomplete;
